/**
 * Fuel & energy efficiency on the Fuel tab, from Samsara's daily reports
 * (zeecore GET /fleet-intelligence/fuel/efficiency). Plan:
 * zeecore/docs/architecture/fuel_energy_efficiency_plan.md, step 2.
 *
 * - MPG is UK MPG, worked out by zeecore from fuel and distance.
 * - Diesel MPG and EV miles per kWh are never mixed.
 * - Vehicles are ranked within their class, so a van is never set against an artic.
 * - A figure Samsara did not report says so; it is never shown as 0.
 * - The driver table says what share of the miles it covers.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, Gauge } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/apiClient';
import { ukTodayYmd } from '../../utils/ukTime';
import LineChart from '../LineChart';

export interface EfficiencySubject {
  subject_entity_id: string;
  name: string;
  vehicle_class?: string;
  energy_type?: string;
  days: number;
  miles: number;
  mpg_uk: number | null;
  litres: number | null;
  miles_per_kwh: number | null;
  kwh: number | null;
  miles_without_figure: number;
  idle_pct: number | null;
  low_mileage: boolean;
  vs_class_pct?: number | null;
}

export interface FuelEfficiencyResponse {
  available: boolean;
  reason?: string;
  days?: { from: string; to: string };
  latest_day?: string | null;
  fleet?: {
    mpg_uk: number | null;
    miles_fuel: number;
    litres: number;
    miles_per_kwh: number | null;
    miles_electric: number;
    electric_without_energy: number;
    idle_pct: number | null;
    idle_hours?: number;
    miles_without_figure: number;
  };
  class_median_mpg?: Record<string, number>;
  /** Where to start: vehicles and card rows that stand out, worst first (zeecore's rules). */
  needs_a_look?: {
    total: number;
    items: Array<{ kind: string; tab: 'vehicles' | 'card'; subject_entity_id: string | null; name: string; reason: string; emphasis?: string }>;
  };
  /** zeecore's Needs a look thresholds, for colouring; absent from an older server, then nothing is amber. */
  thresholds?: { idle_pct: number; below_class_pct: number; card_gap_pct: number };
  /** The same number of UK days just before; null when not compared or nothing stored then. */
  previous?: { days: { from: string; to: string }; mpg_uk: number | null; idle_pct: number | null } | null;
  /** The fleet per UK day; null where a day has no figure. */
  by_day?: Array<{ day: string; mpg_uk: number | null; idle_pct: number | null; miles: number }>;
  vehicles?: EfficiencySubject[];
  drivers?: EfficiencySubject[];
  driver_miles_coverage_pct?: number | null;
  /** Samsara's litres against the fuel card's, per fuel vehicle. */
  card_check?: {
    period_days: number;
    card_latest_day: string | null;
    card_data_ends_early: boolean;
    vehicles: Array<{
      subject_entity_id: string;
      name: string;
      card_litres: number | null;
      card_fills: number;
      samsara_litres: number | null;
      difference_pct: number | null;
      card_mpg_uk: number | null;
      samsara_mpg_uk: number | null;
      status: 'agree' | 'differs' | 'period_too_short' | 'no_card_fills' | 'no_samsara_fuel' | 'samsara_partial' | 'no_samsara_vehicle';
    }>;
  };
}

/** Rows shown in a tab before "Show all". */
export const TAB_ROWS = 10;
/** Whether the trend starts open. Open until the client says otherwise (plan, UI changes, question 3). */
export const TREND_OPEN_BY_DEFAULT = true;

/** First `TAB_ROWS` rows, with a toggle for the rest. */
function useShowAll(): [boolean, () => void] {
  const [all, setAll] = useState(false);
  return [all, () => setAll((v) => !v)];
}

function ShowAllToggle({ all, total, onToggle, testId }: { all: boolean; total: number; onToggle: () => void; testId: string }) {
  if (total <= TAB_ROWS) return null;
  return (
    <button type="button" onClick={onToggle} className="text-xs text-sky-400 hover:text-sky-300" data-testid={testId}>
      {all ? `Show the first ${TAB_ROWS}` : `Show all ${total.toLocaleString()}`}
    </button>
  );
}

/** "mpg 12.1 to 12.6 over the period, idling 10.2% to 11.4%": the trend in one line. */
function trendSummary(days: NonNullable<FuelEfficiencyResponse['by_day']>): string {
  const ends = (pick: 'mpg_uk' | 'idle_pct') => {
    const known = days.filter((d) => d[pick] !== null);
    return known.length >= 2 ? [known[0]![pick]!, known[known.length - 1]![pick]!] : null;
  };
  const mpg = ends('mpg_uk');
  const idle = ends('idle_pct');
  const parts = [
    mpg ? `mpg ${mpg[0]} to ${mpg[1]}` : null,
    idle ? `idling ${idle[0]}% to ${idle[1]}%` : null,
  ].filter(Boolean);
  return parts.length > 0 ? `Trend: ${parts.join(', ')} from first to last day` : 'Trend by day';
}

const CARD_STATUS_TEXT: Record<NonNullable<FuelEfficiencyResponse['card_check']>['vehicles'][number]['status'], string> = {
  agree: 'Within 15%',
  differs: 'Differs by more than 15%',
  period_too_short: 'Period under 14 days: fills and use too far out of step to compare',
  no_card_fills: 'No fuel card fills',
  no_samsara_fuel: 'No fuel figure from Samsara',
  samsara_partial: 'Samsara has no fuel figure for some days driven',
  no_samsara_vehicle: 'On the fuel card but not a Samsara vehicle',
};

type CardCheckRow = NonNullable<FuelEfficiencyResponse['card_check']>['vehicles'][number];

function CardCheckTable({ rows, highlightId }: { rows: CardCheckRow[]; highlightId?: string | null }) {
  const litres = (n: number | null) => (n !== null ? `${n.toLocaleString()} L` : NOT_REPORTED);
  const mpg = (n: number | null) => (n !== null ? `${n}` : NOT_REPORTED);
  return (
    <div className="space-y-2" data-testid="fuel-card-check">
      <div className={TABLE_BOX}>
        <table className="min-w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide text-gray-200">
            <tr>
              <th className={HEAD_CELL}>Vehicle</th>
              <th className={`${HEAD_CELL} text-right`}>Samsara</th>
              <th className={`${HEAD_CELL} text-right`}>Fuel card</th>
              <th className={`${HEAD_CELL} text-right`}>Gap</th>
              <th className={`${HEAD_CELL} text-right`}>mpg (Samsara / card)</th>
              <th className={HEAD_CELL}>Check</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/70">
            {rows.map((v) => (
              <tr
                key={v.subject_entity_id}
                data-row-id={v.subject_entity_id}
                data-highlighted={v.subject_entity_id === highlightId || undefined}
                className={`text-gray-200 ${v.subject_entity_id === highlightId ? HIGHLIGHT_ROW : ''}`}
              >
                <td className="px-3 py-2 font-medium">{v.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{litres(v.samsara_litres)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {litres(v.card_litres)}{v.card_fills > 0 ? ` (${v.card_fills} fill${v.card_fills === 1 ? '' : 's'})` : ''}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{v.difference_pct !== null ? `${v.difference_pct > 0 ? '+' : ''}${v.difference_pct}%` : ''}</td>
                <td className="px-3 py-2 text-right tabular-nums">{mpg(v.samsara_mpg_uk)} / {mpg(v.card_mpg_uk)}</td>
                <td className={`px-3 py-2 text-xs ${v.status === 'differs' || v.status === 'no_samsara_vehicle' ? 'text-amber-300' : 'text-gray-400'}`}>{CARD_STATUS_TEXT[v.status]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Days with a figure only: a day without one is a gap, never plotted as 0. */
function trendPoints(days: NonNullable<FuelEfficiencyResponse['by_day']>, pick: 'mpg_uk' | 'idle_pct') {
  return days
    .filter((d) => d[pick] !== null)
    .map((d) => ({ label: ukDate(d.day).slice(0, 5), value: d[pick] as number, date: d.day }));
}

const NOT_REPORTED = 'Not reported';
/**
 * Each table scrolls in its own box, so its header row can stick at the top of
 * it. overflow-x-auto + overflow-y-auto (not overflow-auto): index.css gives
 * those two classes the app's dark scrollbar; overflow-auto gets the browser's.
 */
const TABLE_BOX = 'max-h-128 overflow-x-auto overflow-y-auto rounded-lg border border-gray-700';
/** Grey, opaque header cells that stay put while the rows scroll under them. */
const HEAD_CELL = 'sticky top-0 z-10 bg-gray-700 px-3 py-2';
/** A row picked from Needs a look. */
const HIGHLIGHT_ROW = 'bg-emerald-900/40 ring-1 ring-inset ring-emerald-400/70';
/** Only finished UK days are imported, so today's figures are never there yet. */
const NEXT_MORNING = "Samsara's figures for a day arrive the next morning.";

function efficiencyText(s: EfficiencySubject): string {
  if (s.energy_type === 'electric') {
    return s.miles_per_kwh !== null ? `${s.miles_per_kwh} mi/kWh` : 'No energy data from Samsara';
  }
  return s.mpg_uk !== null ? `${s.mpg_uk} mpg` : NOT_REPORTED;
}

function partialNote(s: EfficiencySubject): string | null {
  if (s.miles_without_figure <= 0) return null;
  const hasFigure = s.energy_type === 'electric' ? s.miles_per_kwh !== null : s.mpg_uk !== null;
  return hasFigure ? `Leaves out ${s.miles_without_figure.toLocaleString()} mi with no figure` : null;
}

function ukDate(day: string | null | undefined): string {
  if (!day) return '';
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

function Card({ label, value, note, change }: { label: string; value: string; note?: string | null; change?: { text: string; tone: 'good' | 'bad' | 'neutral' } | null }) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-white">{value}</div>
      {change && (
        <div
          className={`mt-1 text-xs tabular-nums ${change.tone === 'good' ? 'text-emerald-300' : change.tone === 'bad' ? 'text-amber-300' : 'text-gray-400'}`}
          data-testid={`change-${label}`}
        >
          {change.text}
        </div>
      )}
      {note && <div className="mt-1 text-xs text-gray-400">{note}</div>}
    </div>
  );
}

/**
 * "Up 0.4 on the previous 7 days": the change on the same number of days just
 * before. Unknown on either side gives no change, never a change from 0.
 */
function changeOnPrevious(
  now: number | null,
  before: number | null | undefined,
  previous: FuelEfficiencyResponse['previous'],
  unit: string,
  higherIsBetter: boolean,
): { text: string; tone: 'good' | 'bad' | 'neutral' } | null {
  if (!previous) return null;
  const days = Math.round((Date.parse(previous.days.to) - Date.parse(previous.days.from)) / 86_400_000) + 1;
  const span = `the previous ${days} day${days === 1 ? '' : 's'}`;
  // No current figure: the card already says "Not reported"; there is no change to state.
  if (now === null) return null;
  if (before === null || before === undefined) return { text: `No figure for ${span}`, tone: 'neutral' };
  const diff = Math.round((now - before) * 10) / 10;
  if (diff === 0) return { text: `Same as ${span}`, tone: 'neutral' };
  const up = diff > 0;
  return {
    text: `${up ? 'Up' : 'Down'} ${Math.abs(diff)}${unit} on ${span}`,
    tone: up === higherIsBetter ? 'good' : 'bad',
  };
}

/**
 * A note on a row (a figure that leaves out miles, few miles), as an amber
 * icon beside the name instead of a mostly empty column. Hover shows it;
 * a tap or Enter shows it under the name, for touch and keyboards.
 */
function NoteIcon({ note }: { note: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={note}
        aria-label={note}
        aria-expanded={open}
        className="ml-1.5 inline-flex align-text-bottom text-amber-400 hover:text-amber-300"
        data-testid="row-note"
      >
        <AlertTriangle className="h-4 w-4" aria-hidden />
      </button>
      {open && <span className="block text-xs font-normal text-amber-200/90">{note}</span>}
    </>
  );
}

/**
 * The amber thresholds come from zeecore (`thresholds`), the same numbers that
 * decide Needs a look, so the colours and the list can't drift apart.
 */
type Thresholds = NonNullable<FuelEfficiencyResponse['thresholds']>;

export type SortKey = 'name' | 'efficiency' | 'miles' | 'idle';
export interface SortState { key: SortKey; dir: 'asc' | 'desc' }

function efficiencyValue(s: EfficiencySubject): number | null {
  return s.energy_type === 'electric' ? s.miles_per_kwh : s.mpg_uk;
}

/** Sorted copy; a row with no figure for the key always goes last, whichever way. */
function sortSubjects(rows: EfficiencySubject[], sort: SortState | null): EfficiencySubject[] {
  if (!sort) return rows;
  const value = (s: EfficiencySubject): string | number | null =>
    sort.key === 'name' ? s.name : sort.key === 'efficiency' ? efficiencyValue(s) : sort.key === 'miles' ? s.miles : s.idle_pct;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return sign * (typeof va === 'string' ? va.localeCompare(String(vb)) : va - Number(vb));
  });
}

function SortHeader({ label, sortKey, sort, onSort, align }: {
  label: string; sortKey: SortKey; sort: SortState | null; onSort: (key: SortKey) => void; align: 'left' | 'right';
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={`${HEAD_CELL} ${align === 'right' ? 'text-right' : ''}`}
      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-white ${active ? 'text-white' : ''}`}
      >
        {label}
        {active && (sort!.dir === 'asc' ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />)}
      </button>
    </th>
  );
}

function SubjectTable({ rows, showClass, medians = {}, sort, onSort, highlightId, thresholds }: {
  rows: EfficiencySubject[]; showClass: boolean; medians?: Record<string, number>;
  highlightId?: string | null;
  thresholds?: Thresholds;
  /** When given, the headers sort the table (the Driver tab). */
  sort?: SortState | null; onSort?: (key: SortKey) => void;
}) {
  const header = (label: string, key: SortKey, align: 'left' | 'right') => (onSort
    ? <SortHeader label={label} sortKey={key} sort={sort ?? null} onSort={onSort} align={align} />
    : <th className={`${HEAD_CELL} ${align === 'right' ? 'text-right' : ''}`}>{label}</th>);
  return (
    <div className={TABLE_BOX}>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-gray-200">
          <tr>
            {header(showClass ? 'Vehicle' : 'Driver', 'name', 'left')}
            {header('Efficiency', 'efficiency', 'right')}
            {showClass && <th className={`${HEAD_CELL} text-right`}>vs class</th>}
            {header('Miles', 'miles', 'right')}
            {header('Idle', 'idle', 'right')}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/70">
          {rows.map((s, i) => {
            const classHeader = showClass && s.vehicle_class !== rows[i - 1]?.vehicle_class;
            const median = medians[String(s.vehicle_class)];
            const note = [partialNote(s), s.low_mileage ? 'Few miles: read with care' : null].filter(Boolean).join('. ');
            const known = s.energy_type === 'electric' ? s.miles_per_kwh !== null : s.mpg_uk !== null;
            return (
              <Fragment key={s.subject_entity_id}>
              {classHeader && (
                <tr className="bg-gray-950/80" data-testid={`class-header-${s.vehicle_class}`}>
                  <td colSpan={5} className="px-3 py-1.5 text-xs font-semibold text-gray-300">
                    {s.vehicle_class === 'Default' ? 'Class not set on Assets' : s.vehicle_class}
                    {median ? ` · median ${median} mpg` : ''}
                  </td>
                </tr>
              )}
              <tr
                data-row-id={s.subject_entity_id}
                data-highlighted={s.subject_entity_id === highlightId || undefined}
                className={`text-gray-200 ${s.subject_entity_id === highlightId ? HIGHLIGHT_ROW : ''}`}
              >
                <td className="px-3 py-2 font-medium">{s.name}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${known ? '' : 'text-gray-500'}`}>
                  {efficiencyText(s)}
                  {note && <NoteIcon note={note} />}
                </td>
                {showClass && (
                  <td className={`px-3 py-2 text-right tabular-nums ${thresholds && (s.vs_class_pct ?? 0) <= -thresholds.below_class_pct ? 'text-amber-300' : 'text-gray-400'}`}>
                    {s.vs_class_pct !== null && s.vs_class_pct !== undefined ? `${s.vs_class_pct > 0 ? '+' : ''}${s.vs_class_pct}%` : ''}
                  </td>
                )}
                <td className="px-3 py-2 text-right tabular-nums">{s.miles.toLocaleString()}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${thresholds && s.idle_pct !== null && s.idle_pct > thresholds.idle_pct ? 'text-amber-300' : ''}`}>
                  {s.idle_pct !== null ? `${s.idle_pct}%` : NOT_REPORTED}
                </td>
              </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type DetailTab = 'vehicles' | 'drivers' | 'card';

/** Card check rows that need a look come first; those that agree sit behind a toggle. */
const CARD_ATTENTION_ORDER: CardCheckRow['status'][] = [
  'differs', 'no_samsara_vehicle', 'samsara_partial', 'no_samsara_fuel', 'no_card_fills', 'period_too_short',
];

function DetailTabs({ data, vehicles, drivers, tab, setTab, highlightId }: {
  data: FuelEfficiencyResponse; vehicles: EfficiencySubject[]; drivers: EfficiencySubject[];
  tab: DetailTab; setTab: (t: DetailTab) => void;
  /** The row picked from Needs a look: shown even when beyond the first rows. */
  highlightId: string | null;
}) {
  const [allVehicles, toggleVehicles] = useShowAll();
  const [allDrivers, toggleDrivers] = useShowAll();
  const [lowMileage, setLowMileage] = useState(false);
  // No sort until a header is clicked: zeecore's order (best first) to start.
  const [driverSort, setDriverSort] = useState<SortState | null>(null);
  const onDriverSort = (key: SortKey) => setDriverSort((cur) => (
    cur?.key === key
      ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }
  ));
  const [showAgree, setShowAgree] = useState(false);

  const cardRows = data.card_check?.vehicles ?? [];
  const cardAttention = cardRows
    .filter((r) => r.status !== 'agree')
    .sort((a, b) => CARD_ATTENTION_ORDER.indexOf(a.status) - CARD_ATTENTION_ORDER.indexOf(b.status));
  const cardAgree = cardRows.filter((r) => r.status === 'agree');
  const driversWithMiles = drivers.filter((d) => !d.low_mileage);
  const driversFewMiles = drivers.filter((d) => d.low_mileage);
  const shownDrivers = sortSubjects(lowMileage ? [...driversWithMiles, ...driversFewMiles] : driversWithMiles, driverSort);

  const tabs: Array<{ id: DetailTab; label: string; count: number }> = [
    { id: 'vehicles', label: 'Vehicles', count: vehicles.length },
    ...(drivers.length > 0 ? [{ id: 'drivers' as const, label: 'Drivers', count: drivers.length }] : []),
    ...(cardRows.length > 0 ? [{ id: 'card' as const, label: 'Fuel card check', count: cardAttention.length }] : []),
  ];

  return (
    <div className="space-y-3" data-testid="fuel-efficiency-tabs">
      <div className="flex flex-wrap gap-1 border-b border-gray-700" role="tablist" aria-label="Efficiency detail">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.id ? 'border-emerald-400 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'
            }`}
          >
            {t.label} <span className="tabular-nums text-gray-400">{t.count}</span>
          </button>
        ))}
      </div>

      {tab === 'vehicles' && (
        <div className="space-y-2" role="tabpanel">
          <SubjectTable
            rows={allVehicles || vehicles.findIndex((v) => v.subject_entity_id === highlightId) >= TAB_ROWS ? vehicles : vehicles.slice(0, TAB_ROWS)}
            showClass
            medians={data.class_median_mpg}
            highlightId={highlightId}
            thresholds={data.thresholds}
          />
          <ShowAllToggle all={allVehicles} total={vehicles.length} onToggle={toggleVehicles} testId="fuel-efficiency-vehicles-all" />
        </div>
      )}

      {tab === 'drivers' && (
        <div className="space-y-2" role="tabpanel">
          <p className="text-xs text-gray-400" data-testid="fuel-efficiency-driver-coverage">
            {data.driver_miles_coverage_pct !== null && data.driver_miles_coverage_pct !== undefined
              ? `Covers ${data.driver_miles_coverage_pct}% of vehicle miles: Samsara only credits miles to a driver when it knows who drove.`
              : 'Samsara only credits miles to a driver when it knows who drove.'}
          </p>
          <SubjectTable
            rows={allDrivers ? shownDrivers : shownDrivers.slice(0, TAB_ROWS)}
            showClass={false}
            sort={driverSort}
            onSort={onDriverSort}
            thresholds={data.thresholds}
          />
          <div className="flex flex-wrap gap-4">
            <ShowAllToggle all={allDrivers} total={shownDrivers.length} onToggle={toggleDrivers} testId="fuel-efficiency-drivers-all" />
            {driversFewMiles.length > 0 && (
              <button type="button" onClick={() => setLowMileage((v) => !v)} className="text-xs text-sky-400 hover:text-sky-300" data-testid="fuel-efficiency-drivers-few-miles">
                {lowMileage ? `Hide the ${driversFewMiles.length} with under 50 miles` : `Show ${driversFewMiles.length} with under 50 miles`}
              </button>
            )}
          </div>
        </div>
      )}

      {tab === 'card' && (
        <div className="space-y-2" role="tabpanel">
          <p className="text-xs text-gray-400">
            {cardAttention.length > 0
              ? `${cardAttention.length} to look at, gaps and missing figures first.`
              : 'Every vehicle agrees within 15%.'}
          </p>
          {cardAttention.length > 0 && <CardCheckTable rows={cardAttention} highlightId={highlightId} />}
          {cardAgree.length > 0 && (
            <>
              <button type="button" onClick={() => setShowAgree((v) => !v)} className="text-xs text-sky-400 hover:text-sky-300" data-testid="fuel-card-check-agree">
                {showAgree ? `Hide the ${cardAgree.length} that agree` : `Show ${cardAgree.length} that agree`}
              </button>
              {showAgree && <CardCheckTable rows={cardAgree} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}

type NeedsItem = NonNullable<FuelEfficiencyResponse['needs_a_look']>['items'][number];

/** The reason, with the part past a threshold (zeecore's `emphasis`) in amber. */
function ReasonText({ reason, emphasis }: { reason: string; emphasis?: string }) {
  const at = emphasis ? reason.indexOf(emphasis) : -1;
  if (!emphasis || at < 0) return <>{reason}</>;
  return (
    <>
      {reason.slice(0, at)}
      <span className="font-semibold text-amber-300">{emphasis}</span>
      {reason.slice(at + emphasis.length)}
    </>
  );
}

function NeedsALook({ list, selected, onSelect }: {
  list: NonNullable<FuelEfficiencyResponse['needs_a_look']>;
  selected: string | null;
  onSelect: (item: NeedsItem, key: string) => void;
}) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4" data-testid="fuel-efficiency-needs-a-look">
      <h4 className="text-xs font-semibold uppercase tracking-wide-3 text-gray-400">Needs a look</h4>
      {list.items.length === 0 ? (
        <p className="mt-1 text-sm text-gray-400">Nothing stands out for this period.</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-700/70">
          {list.items.map((item, i) => {
            const key = `${item.kind}-${item.name}-${i}`;
            const isSelected = selected === key;
            return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(item, key)}
                aria-pressed={isSelected}
                className={`flex w-full flex-wrap items-baseline gap-x-3 rounded px-2 py-2 text-left ${isSelected ? 'bg-emerald-900/40 ring-1 ring-inset ring-emerald-400/70' : 'hover:bg-gray-800/60'}`}
              >
                <span className="font-medium text-white">{item.name}</span>
                <span className="text-sm text-gray-300"><ReasonText reason={item.reason} emphasis={item.emphasis} /></span>
                <span className="ml-auto text-xs text-sky-400">{item.tab === 'card' ? 'Fuel card check' : 'Vehicles'}</span>
              </button>
            </li>
            );
          })}
        </ul>
      )}
      {list.total > list.items.length && (
        <p className="mt-1 text-xs text-gray-400">{list.total - list.items.length} more in the tables below.</p>
      )}
    </div>
  );
}

export default function FuelEfficiencySection({ from, to }: { from: string; to: string }) {
  const [data, setData] = useState<FuelEfficiencyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DetailTab>('vehicles');
  const listRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  // A row picked in Needs a look: its key there, and the table row it points at.
  const [selected, setSelected] = useState<{ key: string; rowId: string | null } | null>(null);
  const selectItem = (item: NeedsItem, key: string) => {
    if (selected?.key === key) {
      setSelected(null); // a second click clears it
      return;
    }
    setSelected({ key, rowId: item.subject_entity_id });
    setTab(item.tab);
    // After the tab renders: bring the row into view, or the tabs when there is no single row (EVs).
    window.requestAnimationFrame(() => {
      const row = item.subject_entity_id
        ? tabsRef.current?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(item.subject_entity_id)}"]`)
        : null;
      (row ?? tabsRef.current)?.scrollIntoView?.({ behavior: 'smooth', block: row ? 'center' : 'start' });
    });
  };

  useEffect(() => {
    let cancelled = false;
    // A row picked for the old period may not be in the new one.
    setSelected(null);
    setLoading(true);
    setError(null);
    authenticatedFetch(API_ENDPOINTS.fleetIntelligence.fuelEfficiency({ from, to }))
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        return (await res.json()) as FuelEfficiencyResponse;
      })
      .then((body) => { if (!cancelled) setData(body); })
      .catch(() => { if (!cancelled) setError('Could not load fuel efficiency'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const heading = (
    <div className="flex items-center gap-2">
      <Gauge className="h-5 w-5 text-emerald-400" aria-hidden />
      <h3 className="text-lg font-semibold text-white">Fuel and energy efficiency</h3>
    </div>
  );

  if (loading && !data) {
    return <section className="space-y-3" data-testid="fuel-efficiency">{heading}<p className="text-sm text-gray-400">Loading efficiency…</p></section>;
  }
  if (error) {
    return <section className="space-y-3" data-testid="fuel-efficiency">{heading}<p className="text-sm text-amber-300">{error}</p></section>;
  }
  if (!data?.available || !data.fleet) {
    return (
      <section className="space-y-3" data-testid="fuel-efficiency">
        {heading}
        <p className="text-sm text-gray-400">{data?.reason ?? 'No Samsara fuel and energy figures yet'}</p>
      </section>
    );
  }

  const { fleet } = data;
  const vehicles = data.vehicles ?? [];
  const drivers = data.drivers ?? [];
  // A period running to today can't include today: say so, not just "no figures".
  const reachesToday = (data.days?.to ?? '') >= ukTodayYmd();
  if (vehicles.length === 0) {
    return (
      <section className="space-y-3" data-testid="fuel-efficiency">
        {heading}
        <p className="text-sm text-gray-400">
          No Samsara fuel and energy figures for this period.{reachesToday ? ` ${NEXT_MORNING}` : ''}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="fuel-efficiency">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {heading}
        <span className="text-xs text-gray-400" data-testid="fuel-efficiency-days">
          From Samsara, UK days {ukDate(data.days?.from)} to {ukDate(data.latest_day ?? data.days?.to)}
          {reachesToday ? `. ${NEXT_MORNING}` : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card
          label="Fleet fuel economy"
          value={fleet.mpg_uk !== null ? `${fleet.mpg_uk} mpg` : NOT_REPORTED}
          change={changeOnPrevious(fleet.mpg_uk, data.previous?.mpg_uk, data.previous, ' mpg', true)}
          note={`mpg over ${fleet.miles_fuel.toLocaleString()} mi, fuel vehicles only`}
        />
        <Card
          label="Engine idling"
          value={fleet.idle_pct !== null ? `${fleet.idle_pct}%` : NOT_REPORTED}
          change={changeOnPrevious(fleet.idle_pct, data.previous?.idle_pct, data.previous, ' points', false)}
          note={fleet.idle_hours !== undefined
            ? `${fleet.idle_hours.toLocaleString()} engine hours idling, all vehicles`
            : 'Share of engine running time spent idle, all vehicles'}
        />
        {data.needs_a_look ? (
          <button
            type="button"
            onClick={() => listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })}
            className="text-left"
            data-testid="fuel-efficiency-to-look-at"
          >
            <Card
              label="To look at"
              value={data.needs_a_look.total.toLocaleString()}
              note={data.needs_a_look.total === 0 ? 'Nothing stands out' : 'Vehicles and fuel card rows that stand out, listed below'}
            />
          </button>
        ) : (
          <Card
            label="Electric vehicles"
            value={fleet.miles_per_kwh !== null ? `${fleet.miles_per_kwh} mi/kWh` : 'No energy data'}
            note={fleet.electric_without_energy > 0
              ? `${fleet.electric_without_energy} EV${fleet.electric_without_energy === 1 ? '' : 's'} drove ${fleet.miles_electric.toLocaleString()} mi with no energy figure from Samsara`
              : `${fleet.miles_electric.toLocaleString()} mi`}
          />
        )}
      </div>

      {data.needs_a_look && (
        <div ref={listRef}>
          <NeedsALook list={data.needs_a_look} selected={selected?.key ?? null} onSelect={selectItem} />
        </div>
      )}

      {(data.by_day?.length ?? 0) >= 2 && (
        <details open={TREND_OPEN_BY_DEFAULT} className="group rounded-lg border border-gray-700 bg-gray-800/30" data-testid="fuel-efficiency-trend">
          <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-gray-200" data-testid="fuel-efficiency-trend-summary">
            {trendSummary(data.by_day!)}
          </summary>
          <div className="grid grid-cols-1 gap-4 p-4 pt-0 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
              <h4 className="text-sm font-semibold text-gray-200">Fleet fuel economy by day (UK mpg)</h4>
              <LineChart data={trendPoints(data.by_day!, 'mpg_uk')} title="" heightClass="h-48" chartHeight={180} color="#34d399" xAxisLabel="Date" yAxisLabel="" />
            </div>
            <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
              <h4 className="text-sm font-semibold text-gray-200">Engine idling by day (%)</h4>
              <LineChart data={trendPoints(data.by_day!, 'idle_pct')} title="" heightClass="h-48" chartHeight={180} color="#f59e0b" xAxisLabel="Date" yAxisLabel="" />
            </div>
          </div>
        </details>
      )}

      <div ref={tabsRef}>
        <DetailTabs data={data} vehicles={vehicles} drivers={drivers} tab={tab} setTab={setTab} highlightId={selected?.rowId ?? null} />
      </div>

      <details className="rounded-lg border border-gray-700/70 bg-gray-800/20" data-testid="fuel-efficiency-about">
        <summary className="cursor-pointer select-none px-4 py-2 text-xs font-medium text-gray-400">About these figures</summary>
        <ul className="list-disc space-y-1 px-8 pb-3 text-xs text-gray-400">
          <li>Samsara&apos;s own daily fuel and energy reports, UK days {ukDate(data.days?.from)} to {ukDate(data.latest_day ?? data.days?.to)}.{reachesToday ? ` ${NEXT_MORNING}` : ''}</li>
          <li>Fuel economy is UK mpg, worked out from the fuel Samsara reports and the miles driven. Electric vehicles are shown in miles per kWh and never mixed with mpg.</li>
          <li>A figure Samsara did not report is left out and says so, never counted as 0. Days with no fuel figure are left out of the trend line.</li>
          {data.driver_miles_coverage_pct !== null && data.driver_miles_coverage_pct !== undefined && (
            <li>Drivers cover {data.driver_miles_coverage_pct}% of vehicle miles: Samsara only credits miles to a driver when it knows who drove.</li>
          )}
          <li>The fuel card check sets Samsara&apos;s litres beside the card&apos;s for the same days. Neither is taken as right: a gap is a question to look into (a missing card, fuel bought elsewhere, or a sensor).</li>
          {data.card_check?.card_data_ends_early && data.card_check.card_latest_day && (
            <li className="text-amber-300" data-testid="fuel-card-check-ends-early">
              Fuel card data ends on {ukDate(data.card_check.card_latest_day)}, so card litres for this period are incomplete.
            </li>
          )}
        </ul>
      </details>
    </section>
  );
}
